import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EmptyState, ErrorState, Skeleton } from "../components/ui/Feedback";
import { Input } from "../components/ui/Input";
import { TBody, TD, TH, THead, TR, Table } from "../components/ui/Table";
import { useCollections } from "../features/collections/useCollections";
import { formatDate, formatNumber } from "../lib/format";

const SEARCH_DEBOUNCE_MS = 250;

export const CollectionsPage = () => {
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchInput]);

	const query = useCollections(search);
	const items = query.data?.items ?? [];

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-xl font-semibold tracking-tight text-ink">Collections</h2>
				<p className="mt-1 text-sm text-ink-muted">
					Synced from Shopify. Open one to see what is in it.
				</p>
			</div>

			<Input
				value={searchInput}
				placeholder="Search collections"
				aria-label="Search collections"
				className="max-w-sm"
				onChange={(event) => setSearchInput(event.target.value)}
			/>

			{query.isError ? (
				<ErrorState title="Could not load collections" description={query.error.message} />
			) : null}

			{query.isPending ? <Skeleton className="h-40 w-full" /> : null}

			{query.data && items.length === 0 ? (
				<EmptyState
					title="No collections"
					description="Run a collection sync from Settings to pull them in from Shopify."
				/>
			) : null}

			{items.length > 0 ? (
				<div className="overflow-hidden rounded-md border border-line">
					<Table>
						<THead>
							<TR>
								<TH>Collection</TH>
								<TH className="w-28">Products</TH>
								<TH className="w-48">Updated</TH>
							</TR>
						</THead>
						<TBody>
							{items.map((collection) => (
								<TR key={collection.id} className="hover:bg-raised">
									<TD>
										<div className="flex items-center gap-3">
											{collection.imageUrl ? (
												<img
													src={collection.imageUrl}
													alt=""
													loading="lazy"
													className="h-9 w-9 shrink-0 rounded-md border border-line object-cover"
												/>
											) : (
												<div className="h-9 w-9 shrink-0 rounded-md border border-dashed border-line" />
											)}
											<div className="min-w-0">
												<Link
													to={`/collections/${collection.id}`}
													className="font-medium text-ink underline-offset-4 hover:underline"
												>
													{collection.title ?? "Untitled"}
												</Link>
												<p className="mt-0.5 truncate text-xs text-ink-faint">
													{collection.handle ?? "—"}
												</p>
											</div>
										</div>
									</TD>
									<TD className="text-ink-muted">{formatNumber(collection.productCount)}</TD>
									<TD className="text-xs text-ink-muted">
										{formatDate(collection.shopifyUpdatedAt)}
									</TD>
								</TR>
							))}
						</TBody>
					</Table>
				</div>
			) : null}
		</div>
	);
};
